/**
 * useOKRDialogs Hook
 *
 * Manages all dialog open/close states and form field states
 */

import { useState, useCallback } from 'react';
import { DialogState, DialogFormState } from '../types';

const DEFAULT_DIALOG_STATE: DialogState = {
    createOrgDialog: false,
    createTeamDialog: false,
    editOrgDialog: false,
    addMilestoneDialog: false,
    editMilestoneDialog: false,
    createTeamObjDialog: false,
    feedbackDialog: false,
    editStatusDialog: false,
    noteDialog: false,
    editTeamObjDialog: false,
    editIndObjDialog: false,
    deleteTaskDialog: false,
    createIndObjDialog: false,
    memberViewDialog: false,
    createPeriodDialog: false,
    editPeriodDialog: false,
    deletePeriodDialog: false,
};

const DEFAULT_FORM_STATE: DialogFormState = {
    // Org form fields
    newOrgTitle: '',
    newOrgDescription: '',

    // Team form fields
    newTeamName: '',
    newTeamObjTitle: '',
    newTeamObjDescription: '',

    // Edit team obj fields
    editTeamObjId: null,
    editTeamObjTitle: '',
    editTeamObjMeasure: '',

    // Milestone form fields
    newMilestoneTitle: '',
    newMilestoneDueDate: '',
    newMilestoneStartDate: '',
    newMilestoneAssignTo: '',

    // Individual obj form fields
    newIndObjTitle: '',

    // Note form fields
    newNote: '',
    editNoteId: null,

    // Status edit fields
    newStatus: '',
    newScore: 0,

    // Feedback fields
    feedbackText: '',

    // Delete task
    deleteTaskId: null,

    // Selected items
    selectedObjectiveForEdit: null,
    selectedObjectiveForFeedback: null,
    selectedMilestoneForEdit: null,
    selectedIndObjForEdit: null,
    selectedDeptForTeamOKR: null,
    selectedTeamForMilestone: null,
    selectedMemberForView: null,
    selectedMemberForMilestone: null,

    // Edit period fields
    editPeriodId: null,
    editPeriodName: '',
    editPeriodStartDate: '',
    editPeriodEndDate: '',

    // Delete period fields
    deletePeriodId: null,
    deletePeriodName: '',
};

export interface UseOKRDialogsReturn {
    // Dialog States
    dialogs: DialogState;
    formState: DialogFormState;

    // Generic Dialog Controls
    openDialog: (dialog: keyof DialogState) => void;
    closeDialog: (dialog: keyof DialogState) => void;
    setDialogs: React.Dispatch<React.SetStateAction<DialogState>>;

    // Form State Setters
    setFormState: React.Dispatch<React.SetStateAction<DialogFormState>>;
    updateFormField: <K extends keyof DialogFormState>(field: K, value: DialogFormState[K]) => void;

    // Convenience Methods - Create Org
    openCreateOrgDialog: () => void;
    closeCreateOrgDialog: () => void;

    // Convenience Methods - Edit Org
    openEditOrgDialog: (objective: any) => void;
    closeEditOrgDialog: () => void;

    // Convenience Methods - Create Team
    openCreateTeamDialog: () => void;
    closeCreateTeamDialog: () => void;

    // Convenience Methods - Create Team Objective
    openCreateTeamObjDialog: (deptId: number) => void;
    closeCreateTeamObjDialog: () => void;

    // Convenience Methods - Edit Team Objective
    openEditTeamObjDialog: (teamObj: any) => void;
    closeEditTeamObjDialog: () => void;

    // Convenience Methods - Edit Individual Objective
    openEditIndObjDialog: (indObj: any) => void;
    closeEditIndObjDialog: () => void;

    // Convenience Methods - Create Individual Objective
    openCreateIndObjDialog: (teamObjId: string) => void;
    closeCreateIndObjDialog: () => void;

    // Convenience Methods - Add Milestone
    openAddMilestoneDialog: (teamObjId?: string, memberId?: string) => void;
    closeAddMilestoneDialog: () => void;

    // Convenience Methods - Edit Milestone
    openEditMilestoneDialog: (milestone: any) => void;
    closeEditMilestoneDialog: () => void;

    // Convenience Methods - Edit Status
    openEditStatusDialog: (objective: any, type: 'org' | 'team' | 'individual') => void;
    closeEditStatusDialog: () => void;

    // Convenience Methods - Note
    openNoteDialog: (note?: { id: string; content: string }) => void;
    closeNoteDialog: () => void;

    // Convenience Methods - Feedback
    openFeedbackDialog: (objective: any) => void;
    closeFeedbackDialog: () => void;

    // Convenience Methods - Delete Task
    openDeleteTaskDialog: (taskId: string) => void;
    closeDeleteTaskDialog: () => void;

    // Convenience Methods - View Member
    openMemberView: (member: any) => void;
    closeMemberView: () => void;

    // Convenience Methods - Create Period
    openCreatePeriodDialog: () => void;
    closeCreatePeriodDialog: () => void;

    // Convenience Methods - Edit Period
    openEditPeriodDialog: (period: any) => void;
    closeEditPeriodDialog: () => void;

    // Convenience Methods - Delete Period
    openDeletePeriodDialog: (period: any) => void;
    closeDeletePeriodDialog: () => void;

    // Reset Methods
    resetOrgForm: () => void;
    resetTeamForm: () => void;
    resetMilestoneForm: () => void;
    resetNoteForm: () => void;
    resetFeedbackForm: () => void;
    resetStatusForm: () => void;
    resetAllForms: () => void;
}

export const useOKRDialogs = (): UseOKRDialogsReturn => {
    const [dialogs, setDialogs] = useState<DialogState>(DEFAULT_DIALOG_STATE);
    const [formState, setFormState] = useState<DialogFormState>(DEFAULT_FORM_STATE);

    // Generic Controls
    const openDialog = useCallback((dialog: keyof DialogState) => {
        setDialogs(prev => ({ ...prev, [dialog]: true }));
    }, []);

    const closeDialog = useCallback((dialog: keyof DialogState) => {
        setDialogs(prev => ({ ...prev, [dialog]: false }));
    }, []);

    const updateFormField = useCallback(<K extends keyof DialogFormState>(
        field: K,
        value: DialogFormState[K]
    ) => {
        setFormState(prev => ({ ...prev, [field]: value }));
    }, []);

    // Create Org Dialog
    const openCreateOrgDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createOrgDialog: true }));
    }, []);

    const closeCreateOrgDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createOrgDialog: false }));
        setFormState(prev => ({ ...prev, newOrgTitle: '', newOrgDescription: '' }));
    }, []);

    // Edit Org Dialog
    const openEditOrgDialog = useCallback((objective: any) => {
        setFormState(prev => ({
            ...prev,
            selectedObjectiveForEdit: { ...objective, type: 'org' },
            newOrgTitle: objective.title || '',
            newOrgDescription: objective.description || ''
        }));
        setDialogs(prev => ({ ...prev, editOrgDialog: true }));
    }, []);

    const closeEditOrgDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, editOrgDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedObjectiveForEdit: null,
            newOrgTitle: '',
            newOrgDescription: ''
        }));
    }, []);

    // Create Team Dialog
    const openCreateTeamDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createTeamDialog: true }));
    }, []);

    const closeCreateTeamDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createTeamDialog: false }));
        setFormState(prev => ({ ...prev, newTeamName: '' }));
    }, []);

    // Create Team Objective Dialog
    const openCreateTeamObjDialog = useCallback((deptId: number) => {
        setFormState(prev => ({ ...prev, selectedDeptForTeamOKR: deptId }));
        setDialogs(prev => ({ ...prev, createTeamObjDialog: true }));
    }, []);

    const closeCreateTeamObjDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createTeamObjDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedDeptForTeamOKR: null,
            newTeamObjTitle: '',
            newTeamObjDescription: ''
        }));
    }, []);

    // Edit Team Objective Dialog
    const openEditTeamObjDialog = useCallback((teamObj: any) => {
        setFormState(prev => ({
            ...prev,
            editTeamObjId: teamObj.id,
            editTeamObjTitle: teamObj.title || '',
            editTeamObjMeasure: teamObj.defined_measure || teamObj.measure || ''
        }));
        setDialogs(prev => ({ ...prev, editTeamObjDialog: true }));
    }, []);

    const closeEditTeamObjDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, editTeamObjDialog: false }));
        setFormState(prev => ({
            ...prev,
            editTeamObjId: null,
            editTeamObjTitle: '',
            editTeamObjMeasure: ''
        }));
    }, []);

    // Edit Individual Objective Dialog
    const openEditIndObjDialog = useCallback((indObj: any) => {
        setFormState(prev => ({
            ...prev,
            selectedIndObjForEdit: indObj
        }));
        setDialogs(prev => ({ ...prev, editIndObjDialog: true }));
    }, []);

    const closeEditIndObjDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, editIndObjDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedIndObjForEdit: null
        }));
    }, []);

    // Create Individual Objective Dialog
    const openCreateIndObjDialog = useCallback((teamObjId: string) => {
        setFormState(prev => ({ ...prev, selectedTeamForMilestone: teamObjId }));
        setDialogs(prev => ({ ...prev, createIndObjDialog: true }));
    }, []);

    const closeCreateIndObjDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createIndObjDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedTeamForMilestone: null,
            newIndObjTitle: ''
        }));
    }, []);

    // Add Milestone Dialog
    const openAddMilestoneDialog = useCallback((teamObjId?: string, memberId?: string) => {
        setFormState(prev => ({
            ...prev,
            selectedTeamForMilestone: teamObjId || null,
            selectedMemberForMilestone: memberId || null
        }));
        setDialogs(prev => ({ ...prev, addMilestoneDialog: true }));
    }, []);

    const closeAddMilestoneDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, addMilestoneDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedTeamForMilestone: null,
            selectedMemberForMilestone: null,
            newMilestoneTitle: '',
            newMilestoneDueDate: '',
            newMilestoneStartDate: '',
            newMilestoneAssignTo: ''
        }));
    }, []);

    // Edit Milestone Dialog
    const openEditMilestoneDialog = useCallback((milestone: any) => {
        setFormState(prev => ({
            ...prev,
            selectedMilestoneForEdit: milestone
        }));
        setDialogs(prev => ({ ...prev, editMilestoneDialog: true }));
    }, []);

    const closeEditMilestoneDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, editMilestoneDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedMilestoneForEdit: null
        }));
    }, []);

    // Edit Status Dialog
    const openEditStatusDialog = useCallback((objective: any, type: 'org' | 'team' | 'individual') => {
        setFormState(prev => ({
            ...prev,
            selectedObjectiveForEdit: { ...objective, type },
            newStatus: objective.status || 'In Progress',
            newScore: objective.score || 0
        }));
        setDialogs(prev => ({ ...prev, editStatusDialog: true }));
    }, []);

    const closeEditStatusDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, editStatusDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedObjectiveForEdit: null,
            newStatus: '',
            newScore: 0
        }));
    }, []);

    // Note Dialog
    const openNoteDialog = useCallback((note?: { id: string; content: string }) => {
        if (note) {
            // Edit mode - populate with existing note data
            setFormState(prev => ({
                ...prev,
                newNote: note.content,
                editNoteId: note.id
            }));
        } else {
            // Create mode - clear form
            setFormState(prev => ({
                ...prev,
                newNote: '',
                editNoteId: null
            }));
        }
        setDialogs(prev => ({ ...prev, noteDialog: true }));
    }, []);

    const closeNoteDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, noteDialog: false }));
        setFormState(prev => ({ ...prev, newNote: '', editNoteId: null }));
    }, []);

    // Feedback Dialog
    const openFeedbackDialog = useCallback((objective: any) => {
        setFormState(prev => ({
            ...prev,
            selectedObjectiveForFeedback: objective,
            feedbackText: ''
        }));
        setDialogs(prev => ({ ...prev, feedbackDialog: true }));
    }, []);

    const closeFeedbackDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, feedbackDialog: false }));
        setFormState(prev => ({
            ...prev,
            selectedObjectiveForFeedback: null,
            feedbackText: ''
        }));
    }, []);

    // Delete Task Dialog
    const openDeleteTaskDialog = useCallback((taskId: string) => {
        setFormState(prev => ({ ...prev, deleteTaskId: taskId }));
        setDialogs(prev => ({ ...prev, deleteTaskDialog: true }));
    }, []);

    const closeDeleteTaskDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, deleteTaskDialog: false }));
        setFormState(prev => ({ ...prev, deleteTaskId: null }));
    }, []);

    // View Member
    const openMemberView = useCallback((member: any) => {
        setFormState(prev => ({ ...prev, selectedMemberForView: member }));
        setDialogs(prev => ({ ...prev, memberViewDialog: true }));
    }, []);

    const closeMemberView = useCallback(() => {
        setDialogs(prev => ({ ...prev, memberViewDialog: false }));
        setFormState(prev => ({ ...prev, selectedMemberForView: null }));
    }, []);

    // Create Period Dialog
    const openCreatePeriodDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createPeriodDialog: true }));
    }, []);

    const closeCreatePeriodDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, createPeriodDialog: false }));
    }, []);

    // Edit Period Dialog
    const openEditPeriodDialog = useCallback((period: any) => {
        setFormState(prev => ({
            ...prev,
            editPeriodId: period?.id || null,
            editPeriodName: period?.name || '',
            editPeriodStartDate: period?.start_date
                ? new Date(period.start_date).toISOString().split('T')[0]
                : '',
            editPeriodEndDate: period?.end_date
                ? new Date(period.end_date).toISOString().split('T')[0]
                : '',
        }));
        setDialogs(prev => ({ ...prev, editPeriodDialog: true }));
    }, []);

    const closeEditPeriodDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, editPeriodDialog: false }));
        setFormState(prev => ({
            ...prev,
            editPeriodId: null,
            editPeriodName: '',
            editPeriodStartDate: '',
            editPeriodEndDate: '',
        }));
    }, []);

    // Delete Period Dialog
    const openDeletePeriodDialog = useCallback((period: any) => {
        setFormState(prev => ({
            ...prev,
            deletePeriodId: period?.id || null,
            deletePeriodName: period?.name || '',
        }));
        setDialogs(prev => ({ ...prev, deletePeriodDialog: true }));
    }, []);

    const closeDeletePeriodDialog = useCallback(() => {
        setDialogs(prev => ({ ...prev, deletePeriodDialog: false }));
        setFormState(prev => ({
            ...prev,
            deletePeriodId: null,
            deletePeriodName: '',
        }));
    }, []);

    // Reset Methods
    const resetOrgForm = useCallback(() => {
        setFormState(prev => ({
            ...prev,
            newOrgTitle: '',
            newOrgDescription: ''
        }));
    }, []);

    const resetTeamForm = useCallback(() => {
        setFormState(prev => ({
            ...prev,
            newTeamName: '',
            newTeamObjTitle: '',
            newTeamObjDescription: '',
            selectedDeptForTeamOKR: null
        }));
    }, []);

    const resetMilestoneForm = useCallback(() => {
        setFormState(prev => ({
            ...prev,
            newMilestoneTitle: '',
            newMilestoneDueDate: '',
            newMilestoneStartDate: '',
            newMilestoneAssignTo: '',
            selectedTeamForMilestone: null,
            selectedMemberForMilestone: null
        }));
    }, []);

    const resetNoteForm = useCallback(() => {
        setFormState(prev => ({ ...prev, newNote: '' }));
    }, []);

    const resetFeedbackForm = useCallback(() => {
        setFormState(prev => ({
            ...prev,
            feedbackText: '',
            selectedObjectiveForFeedback: null
        }));
    }, []);

    const resetStatusForm = useCallback(() => {
        setFormState(prev => ({
            ...prev,
            newStatus: '',
            newScore: 0,
            selectedObjectiveForEdit: null
        }));
    }, []);

    const resetAllForms = useCallback(() => {
        setFormState(DEFAULT_FORM_STATE);
    }, []);

    return {
        // Dialog States
        dialogs,
        formState,

        // Generic Dialog Controls
        openDialog,
        closeDialog,
        setDialogs,

        // Form State Setters
        setFormState,
        updateFormField,

        // Convenience Methods - Create Org
        openCreateOrgDialog,
        closeCreateOrgDialog,

        // Convenience Methods - Edit Org
        openEditOrgDialog,
        closeEditOrgDialog,

        // Convenience Methods - Create Team
        openCreateTeamDialog,
        closeCreateTeamDialog,

        // Convenience Methods - Create Team Objective
        openCreateTeamObjDialog,
        closeCreateTeamObjDialog,

        // Convenience Methods - Edit Team Objective
        openEditTeamObjDialog,
        closeEditTeamObjDialog,

        // Convenience Methods - Edit Individual Objective
        openEditIndObjDialog,
        closeEditIndObjDialog,

        // Convenience Methods - Create Individual Objective
        openCreateIndObjDialog,
        closeCreateIndObjDialog,

        // Convenience Methods - Add Milestone
        openAddMilestoneDialog,
        closeAddMilestoneDialog,

        // Convenience Methods - Edit Milestone
        openEditMilestoneDialog,
        closeEditMilestoneDialog,

        // Convenience Methods - Edit Status
        openEditStatusDialog,
        closeEditStatusDialog,

        // Convenience Methods - Note
        openNoteDialog,
        closeNoteDialog,

        // Convenience Methods - Feedback
        openFeedbackDialog,
        closeFeedbackDialog,

        // Convenience Methods - Delete Task
        openDeleteTaskDialog,
        closeDeleteTaskDialog,

        // Convenience Methods - View Member
        openMemberView,
        closeMemberView,

        // Convenience Methods - Create Period
        openCreatePeriodDialog,
        closeCreatePeriodDialog,

        // Convenience Methods - Edit Period
        openEditPeriodDialog,
        closeEditPeriodDialog,

        // Convenience Methods - Delete Period
        openDeletePeriodDialog,
        closeDeletePeriodDialog,

        // Reset Methods
        resetOrgForm,
        resetTeamForm,
        resetMilestoneForm,
        resetNoteForm,
        resetFeedbackForm,
        resetStatusForm,
        resetAllForms,
    };
};

export default useOKRDialogs;
